import * as Sentry from '@sentry/react';
import { createFileLogger } from './logger';
import { parseJwtPayload } from './jwtUtils';
import {
  ANALYTICS_SESSION_KEY,
  ANALYTICS_SESSION_ACTIVITY_KEY,
  analytics,
} from '../services/analytics/analytics';
import { resetAuthCompleteCoordinator } from '../components/Auth/authCompleteCoordinator';
import { LOGIN_PAGE_ERROR_KEY, LOGIN_PAGE_EMAIL_KEY, LOGIN_PAGE_METHOD_KEY } from '../config/authRoutes';
import { clearPinnedSessionTokens, wipeAuth0SpaCache } from './authTokenGuard';
import { dumpAuthDebug } from './authDebug';

const log = createFileLogger('LOGOUT_UTILS');

const ACCOUNTS_INDEX_KEY = 'river_accounts';
const ACCOUNT_PREFIX = 'river_account_';

/**
 * Remove the currently-active account from the multi-account switcher.
 * Must be called BEFORE the authToken is cleared so we can still read the userId.
 */
function removeActiveAccountFromSwitcher(): void {
  try {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    const payload = parseJwtPayload(authToken);
    const userId = payload?.sub;
    if (!userId) return;

    localStorage.removeItem(`${ACCOUNT_PREFIX}${userId}`);

    const raw = localStorage.getItem(ACCOUNTS_INDEX_KEY);
    const ids: string[] = raw ? JSON.parse(raw) : [];
    const updated = ids.filter(id => id !== userId);
    if (updated.length > 0) {
      localStorage.setItem(ACCOUNTS_INDEX_KEY, JSON.stringify(updated));
    } else {
      localStorage.removeItem(ACCOUNTS_INDEX_KEY);
    }

    log.log(`🧹 [LOGOUT] Removed account ${userId} from multi-account switcher`);
  } catch (e) {
    // Non-fatal — don't block the logout flow
    log.warn('[LOGOUT] Failed to remove account from switcher:', e);
  }
}

/**
 * Auth-related storage keys — single source of truth for logout cleanup.
 *
 * Add every key here when you introduce it. Keys are split by storage type so
 * clearAllAuthState can iterate them correctly.
 */
export const AUTH_STORAGE_KEYS = {
  // ── localStorage ──────────────────────────────────────────────────────────
  localStorage: {
    // Auth tokens
    tokens: ['authToken', 'id_token', 'refresh_token'],

    // Pending OAuth context (set before OAuth popup, consumed after)
    pending: [
      'pendingFunnelId',
      'pendingPersona',
      'pendingPageGroup',
      'pendingPageSlug',
      'pendingRedirectTo',
      'pendingTemplateId',
      'pendingIsSignup',
      'pendingWorkflowContext',
      'pendingWorkflowContext_timestamp',
      'pre_signup_onboarding_answers',
    ],

    // Session / redirect data
    session: [
      'redirectAfterLogin',
      'lastAuthRedirect',
      'userSpaceId',
      'userWelcomeDocId',
      'signupEmail',
      // Persisted dataspace context (zustand persist key from
      // dataspaceContextStore). Must be cleared on logout — without this,
      // a same-browser User-A → User-B login would rehydrate User A's
      // orgId for the brief window before App.tsx's /user/context fetch
      // returns, causing one wasted REST round-trip and a flicker of
      // "no access" state from the backend's is_org_member check.
      'dataspace-context',
      // Getting Started widget dismiss is per-user; wipe on logout so a
      // later signup on this browser is not hidden by the previous account.
      'onboarding-widget-storage',
    ],

    // GalBox status
    galbox: ['galbox-inactive', 'galbox-inactive-name'],

    // Guest-preview session flags (see hooks/useGuestMode.ts) + the legacy
    // pre-auth guest upload session (see utils/guestSessionUtils.ts). Must be
    // cleared on every logout so a stale guest flag never survives to
    // misclassify whoever logs into this browser next as a guest.
    guest: ['isGuest', 'guestPackId', 'guestSpaceId', 'guest_session_id'],
  },

  // ── sessionStorage ────────────────────────────────────────────────────────
  sessionStorage: [
    'pendingRedirectTo',
    'pendingTemplateId',
    'landing_hero_context',
    'onboarding_source',
    // Guest-preview seed chat message (see hooks/useGuestMode.ts).
    'guestSeedChatMessage',
    // Analytics session keys — must be cleared so the post-logout session
    // gets a fresh ID and does not inherit the pre-logout session.
    ANALYTICS_SESSION_KEY,
    ANALYTICS_SESSION_ACTIVITY_KEY,
    // One-shot error banner on /login after a failed /auth/complete.
    LOGIN_PAGE_ERROR_KEY,
    LOGIN_PAGE_EMAIL_KEY,
    LOGIN_PAGE_METHOD_KEY,
  ],
} as const;

/**
 * Clear ALL auth-related data from localStorage and sessionStorage.
 */
export function clearAllAuthState(): void {
  log.log('🧹 [LOGOUT] Starting comprehensive auth state cleanup...');

  // In-memory (not storage-backed) — reset so a subsequent sign-in within the
  // same SPA session isn't permanently blocked from either side handling it.
  resetAuthCompleteCoordinator();
  clearPinnedSessionTokens();

  let clearedCount = 0;

  // Clear all localStorage keys
  const lsKeys = Object.values(AUTH_STORAGE_KEYS.localStorage).flat() as string[];
  lsKeys.forEach(key => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared localStorage: ${key}`);
    }
  });

  // Clear Auth0 SDK cache entries (@@auth0spajs@@::...) which live in localStorage
  // when cacheLocation='localstorage'. The Auth0 SDK manages its own key names so
  // we sweep by prefix rather than maintaining a hardcoded key.
  const auth0KeysBefore = Object.keys(localStorage).filter(key => key.startsWith('@@auth0spajs@@')).length;
  wipeAuth0SpaCache();
  if (auth0KeysBefore > 0) {
    clearedCount += auth0KeysBefore;
    log.log(`  ✓ Cleared Auth0 SDK cache (${auth0KeysBefore} keys)`);
  }

  // Clear all sessionStorage keys
  AUTH_STORAGE_KEYS.sessionStorage.forEach(key => {
    if (sessionStorage.getItem(key) !== null) {
      sessionStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared sessionStorage: ${key}`);
    }
  });

  log.log(`🧹 [LOGOUT] Cleanup complete - cleared ${clearedCount} items`);
}

/**
 * Compute the localStorage queue keys belonging to the currently-active user.
 *
 * Mirrors offlineEntityQueue._getStorageKey: the keyspace is
 *   `river_offline_entity_queue_<sub>`  (namespaced — preferred)
 *   `river_offline_entity_queue`        (fallback when no auth token)
 *
 * Returns BOTH so we can wipe whichever this session was actually using —
 * but never the namespaced keys of OTHER signed-in users (the multi-account
 * switcher keeps those alive across a single-user logout).
 */
function _activeUserQueueKeys(): string[] {
  const keys: string[] = ['river_offline_entity_queue'];
  try {
    const token = localStorage.getItem('authToken');
    if (token) {
      const payload = parseJwtPayload(token);
      if (payload?.sub) {
        keys.push(`river_offline_entity_queue_${payload.sub}`);
      }
    }
  } catch { /* ignore — fallback key alone is fine */ }
  return keys;
}

/**
 * Drop in-memory entity caches and wipe every River-owned IndexedDB database.
 * Awaited by the logout flow so the navigation does not race the wipe.
 *
 * @param queueKeysToWipe localStorage keys for the active user's offline
 *   queue, captured BEFORE clearAllAuthState removes the auth token.
 * @returns the count of IDB databases successfully deleted.
 */
async function wipeClientCaches(queueKeysToWipe: string[]): Promise<number> {
  const [
    { useSpaceEntityStore },
    { useEntityByIdCache },
    { useGlobalEntityStore },
    { wipeRiverIDBs },
  ] = await Promise.all([
    import('../stores/spaceEntityStore'),
    import('../stores/entityByIdCache'),
    import('../stores/globalEntityStore'),
    import('../services/idb/idbCleanup'),
  ]);
  useSpaceEntityStore.getState().clearAll();
  useEntityByIdCache.getState().clear();
  // Drop the in-memory global cache (Spaces/Integrations/MCPConnections from
  // the outgoing user). The IDB-backed snapshot is removed by wipeRiverIDBs;
  // this resets the React-visible state so a same-tab login as a different
  // user does not see the previous user's spaces in the brief moment before
  // navigation completes.
  await useGlobalEntityStore.getState().reset();

  // Drop ONLY the active user's offline create queue. Anything queued was
  // never sent to the server, so it is acceptable to drop on explicit logout;
  // re-logging in as the same user would otherwise replay queued items
  // against a workspace state that may have moved on (parents deleted, etc.).
  let removed = 0;
  for (const key of queueKeysToWipe) {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      removed += 1;
    }
  }
  if (removed > 0) {
    log.log(`🧹 [LOGOUT] Cleared ${removed} offline entity queue entries`);
  }

  return wipeRiverIDBs();
}

export interface CompleteLogoutOptions {
  auth0Logout?: (params: any) => void;
  hasAuth0User: boolean;
  /**
   * Clear the Auth0 SPA SDK locally (`openUrl: false`) without federated
   * `/v2/logout` (which would bounce `returnTo: origin` onto marketing `/`).
   * After the local clear, we still navigate to `redirectTo` ourselves.
   */
  auth0LocalOnly?: boolean;
  /**
   * Destination after teardown. Ignored when Auth0 federated logout runs
   * (Auth0 navigates to `origin` itself). Defaults to `/`.
   */
  redirectTo?: string;
  /**
   * Restore this value to `localStorage.redirectAfterLogin` after
   * `clearAllAuthState` wipes it, so the next successful login returns
   * the user to the page they were on.
   */
  preserveRedirectAfterLogin?: string;
}

/**
 * Generation counter so a signup/login that wins the race against an in-flight
 * expiry teardown can cancel the late `window.location` redirect. `clearAllAuthState`
 * may already have run; the new session's `storeTokens` rewrites the keys.
 */
let logoutGeneration = 0;

/** Cancel a logout that has started but not yet navigated away. */
export function cancelPendingLogout(): void {
  logoutGeneration += 1;
  log.log('🚪 [LOGOUT] Pending logout cancelled — a new session was established');
}

/**
 * Perform a complete logout:
 * 1. Clear all localStorage/sessionStorage
 * 2. Drop in-memory entity caches + wipe River IDB databases (awaited)
 * 3. Optionally call Auth0 logout
 * 4. Redirect to `redirectTo` (default `/`), unless Auth0 handles navigation
 *
 * Returns a promise that resolves once the wipe has completed (navigation will
 * have started by then). Callers may fire-and-forget or await — the navigation
 * is unconditional.
 */
export async function performCompleteLogout(options: CompleteLogoutOptions): Promise<void> {
  const generation = logoutGeneration;
  log.log('🚪 [LOGOUT] Starting complete logout process...');
  dumpAuthDebug('performCompleteLogout start', {
    redirectTo: options.redirectTo ?? '/',
    auth0LocalOnly: !!options.auth0LocalOnly,
    hasAuth0User: !!options.hasAuth0User,
    generation,
  });

  // Step 0: Capture the active user's per-account state (queue keys, account ID)
  // BEFORE clearAllAuthState wipes the authToken — the JWT `sub` claim is the
  // only way to derive these keys.
  const queueKeysToWipe = _activeUserQueueKeys();
  removeActiveAccountFromSwitcher();

  // Step 1: Reset analytics facade (emits session_ended, rotates session ID).
  // Must run before clearAllAuthState so the authToken is still available for
  // the session_ended beacon payload.
  analytics.reset();

  // Clear the Sentry user context so the post-logout session isn't attributed
  // to the previous user.
  Sentry.setUser(null);

  // Drop Getting Started dismiss before storage wipe so persist cannot
  // rewrite a previous account's `dismissed: true` after the key is removed.
  try {
    const { useOnboardingWidgetStore } = await import('../stores/onboardingWidgetStore');
    useOnboardingWidgetStore.getState().resetForNewUser();
  } catch (e) {
    log.warn('[LOGOUT] Failed to reset onboarding widget store:', e);
  }

  // Step 2: Clear all auth state
  clearAllAuthState();

  // Restore the return-to path after the wipe. Must happen before navigation
  // so the next login can read it (handleLoginRedirect / AuthenticationGate).
  if (options.preserveRedirectAfterLogin) {
    try {
      localStorage.setItem('redirectAfterLogin', options.preserveRedirectAfterLogin);
    } catch (e) {
      log.warn('[LOGOUT] Failed to restore redirectAfterLogin:', e);
    }
  }

  // Step 2b: Tell the Electron main process to drop its in-process token cache.
  // Without this, the main process keeps using the stale token for heartbeats
  // until it expires (intermittent 401s, no observable hard failure) because
  // `pushTokenToMain` in index.tsx no longer pushes null values to avoid
  // clobbering the cache during renderer-reload races. Browser builds skip
  // this silently — riverOS.auth is only injected by the Electron preload.
  try {
    (window as any).riverOS?.auth?.clearToken?.();
  } catch (e) {
    log.warn('[LOGOUT] Failed to clear Electron auth-token cache:', e);
  }

  // Step 2c: Tear down every warm Desktop's CRDT connection (Y.Doc + IDB +
  // WebSocket provider) and clear the warm-desktop LRU bookkeeping. Must run
  // BEFORE wipeClientCaches's IDB wipe below — otherwise up to
  // MAX_WARM_DESKTOPS connections stay open and can keep writing into the
  // IndexedDB databases mid-wipe (a resurrection race: a live provider
  // recreates a DB entry right after wipeRiverIDBs deletes it).
  try {
    const [{ spaceCRDTService }, { sharedSpaceWindowsCRDTService }, { desktopWarmCache }] = await Promise.all([
      import('../services/crdt/spaceCRDTService'),
      import('../services/crdt/sharedSpaceWindowsCRDTService'),
      import('../services/crdt/desktopWarmCache'),
    ]);
    spaceCRDTService.disconnectAll();
    sharedSpaceWindowsCRDTService.disconnectAll();
    desktopWarmCache.reset();
  } catch (e) {
    log.warn('[LOGOUT] Failed to disconnect warm Desktop CRDT connections:', e);
  }

  // Step 3: Wipe client caches + IDB. Awaited so the redirect below does not
  // race the deletes — losing the wipe would mean cached entity data survives
  // a logout, which is contrary to the security expectation.
  try {
    const deleted = await wipeClientCaches(queueKeysToWipe);
    log.log(`🧹 [LOGOUT] Wiped River IDBs (${deleted} databases)`);
  } catch (e) {
    log.warn('[LOGOUT] Failed to wipe River IDBs:', e);
  }

  if (generation !== logoutGeneration) {
    log.log('🚪 [LOGOUT] Skipping redirect — a new session was established during teardown');
    return;
  }

  // Step 4: Auth0 logout if applicable
  if (options.hasAuth0User && options.auth0Logout && !options.auth0LocalOnly) {
    log.log('🚪 [LOGOUT] Performing Auth0 federated logout...');
    options.auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    return;
  }

  if (options.auth0LocalOnly && options.auth0Logout) {
    log.log('🚪 [LOGOUT] Clearing Auth0 SDK locally (no federated redirect)...');
    try {
      options.auth0Logout({ openUrl: false });
    } catch (e) {
      log.warn('[LOGOUT] Auth0 local logout failed:', e);
    }
  }

  const redirectTo = options.redirectTo || '/';
  log.log('🚪 [LOGOUT] Redirecting to', redirectTo);
  window.location.href = redirectTo;
}

